import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  useTheme,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { GlassCard } from "../../components/ui/glass-card";
import { PoweredByFooter } from "../../components/PoweredByFooter";
import { apiClient, axiosInstance } from "../../services/apiClient";
import { ResendVerificationRequest } from "../../generated/api-client";
import toast from "react-hot-toast";

const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const verificationSentRef = useRef(false);
  const [status, setStatus] = useState<"idle" | "ok" | "error">("idle");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = searchParams.get("token");
    if (!token || verificationSentRef.current) return;
    verificationSentRef.current = true;
    const verifyEmail = async () => {
      try {
        await axiosInstance.get(`/auth/verify-email?token=${encodeURIComponent(token)}`);
        setStatus("ok");
      } catch (err: any) {
        setStatus("error");
        toast.error(err?.response?.data?.detail || "Link non valido o scaduto");
      }
    };
    verifyEmail();
  }, [searchParams]);

  const resendEmail = async () => {
    if (!email.trim()) return toast.error("Inserisci la tua email per reinviare la verifica.");
    setLoading(true);
    try {
      const request = new ResendVerificationRequest({ email: email.trim() });
      await apiClient.resend_verification_auth_resend_verification_post(request);
      toast.success("Se l'email è corretta, riceverai le istruzioni per la verifica.");
    } catch {
      toast.error("Errore nell'invio della mail di verifica.");
    } finally {
      setLoading(false);
    }
  };

  const primaryColor = theme.palette.primary.main;
  const primaryLightColor = theme.palette.primary.light;

  return (
    <Box sx={{ minHeight: "100vh", position: "relative", display: "flex", alignItems: "center", justifyContent: "center", p: 2, backgroundColor: theme.palette.background.default }}>
      <GlassCard hover={false} className="w-full p-4 md:p-8 relative z-10 w-[28rem]">
        <Button
          variant="text"
          startIcon={<ArrowBack />}
          onClick={() => navigate("/login")}
          sx={{ position: "absolute", top: 16, left: 16, color: theme.palette.text.primary, minWidth: "auto" }}
        />
        <div className="flex justify-center mb-6">
          <img src="/assets/long_light.png" alt="logo" className="h-12 rounded-lg" />
        </div>

        {status === "idle" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, textAlign: "center" }}>
            <Typography sx={{ color: theme.palette.text.secondary }}>Controlla la tua email per il link di verifica.</Typography>
            <TextField label="Email" value={email} onChange={(e) => setEmail(e.target.value)} fullWidth disabled={loading} />
            <Button
              onClick={resendEmail}
              variant="contained"
              disabled={loading}
              sx={{ background: `linear-gradient(135deg, ${primaryLightColor}, ${primaryColor})` }}
            >
              {loading ? <CircularProgress size={24} sx={{ color: "white" }} /> : "Reinvia"}
            </Button>
          </Box>
        )}

        {status === "ok" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, textAlign: "center" }}>
            <Alert severity="success" sx={{ bgcolor: `${primaryColor}15` }}>
              Email verificata! Ora puoi accedere.
            </Alert>
            <Button onClick={() => navigate("/login")} variant="contained">
              Vai al Login
            </Button>
          </Box>
        )}

        {status === "error" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, textAlign: "center" }}>
            <Alert severity="error">Link non valido o scaduto.</Alert>
            <Button onClick={() => navigate("/login")} variant="outlined">
              Torna al Login
            </Button>
          </Box>
        )}
      </GlassCard>

      <PoweredByFooter absolute />
    </Box>
  );
};

export default VerifyEmailPage;

